import AddIcon from '@mui/icons-material/Add';
import DeleteRoundedIcon from '@mui/icons-material/DeleteRounded';
import * as React from 'react';
import {
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Tooltip,
  Typography,
  List,
  ListItem,
  ListItemText,
  Grid,
  TextField,
  Divider,
  alpha,
} from '@mui/material';
import { DateTime } from 'luxon';

// GoSharesDatagrid: preserves the original contract (initialRows, handleSaveChanges)
// but uses dialog-like visuals (title, content, actions) inspired by CO2CoefficientDialog.
export default function GoSharesDatagrid(props) {
  const { initialRows = [], handleSaveChanges, onClose, open } = props;

  // Normalize initial rows into entries: { timestamp: ISO, percentage: fraction }
  const normalize = (r) => {
    const ts =
      r.timestamp || r.timestamp_start || r.timestamp_from || r.start || null;
    let p =
      r.percentage !== undefined ? Number(r.percentage) : Number(r.value ?? 0);
    if (Number.isNaN(p)) p = 0;
    // If percentage looks like a percent (e.g. 50), convert to fraction
    if (p > 1) p = p / 100;
    return {
      timestamp: ts ? DateTime.fromISO(ts).toISO() : DateTime.now().toISO(),
      percentage: p,
    };
  };

  const initialSnapshot = React.useMemo(() => {
    return (initialRows || [])
      .map((r) => normalize(r))
      .sort(
        (a, b) =>
          DateTime.fromISO(a.timestamp).toMillis() -
          DateTime.fromISO(b.timestamp).toMillis(),
      );
  }, [initialRows]);

  const [entries, setEntries] = React.useState(initialSnapshot);
  const [draft, setDraft] = React.useState({ percentage: '', date: '' });
  const [error, setError] = React.useState('');

  React.useEffect(() => {
    setEntries(initialSnapshot);
  }, [initialSnapshot]);

  const validateDraft = () => {
    const { percentage, date } = draft;
    if (!percentage && percentage !== 0) {
      setError('Angiv en procentværdi.');
      return false;
    }
    if (!date) {
      setError('Angiv en dato.');
      return false;
    }
    const p = Number(percentage);
    if (Number.isNaN(p)) {
      setError('Ugyldig procent.');
      return false;
    }
    const s = DateTime.fromISO(date);
    if (!s.isValid) {
      setError('Ugyldig dato.');
      return false;
    }
    // check overlaps
    const newStart = s;
    const overlaps = entries.some(
      (e) => DateTime.fromISO(e.timestamp).toMillis() === newStart.toMillis(),
    );
    if (overlaps) {
      setError('Der findes allerede en post for denne dato.');
      return false;
    }
    setError('');
    return true;
  };

  const addEntry = () => {
    if (!validateDraft()) return;
    const p = Number(draft.percentage);
    const fraction = p > 1 ? p / 100 : p;
    const ts = DateTime.fromISO(draft.date).toISO();
    const next = [...entries, { timestamp: ts, percentage: fraction }].sort(
      (a, b) =>
        DateTime.fromISO(a.timestamp).toMillis() -
        DateTime.fromISO(b.timestamp).toMillis(),
    );
    setEntries(next);
    setDraft({ percentage: '', date: '' });
    // Persist immediately like original behavior
    handleSaveChanges(
      next.map((e) => ({ timestamp: e.timestamp, value: e.percentage })),
    );
  };

  const removeEntry = (idx) => {
    const next = entries.filter((_, i) => i !== idx);
    setEntries(next);
    handleSaveChanges(
      next.map((e) => ({ timestamp: e.timestamp, value: e.percentage })),
    );
  };

  return (
    <Dialog open={open} maxWidth="md" fullWidth onClose={onClose}>
      <DialogTitle>GO Andel</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" gutterBottom>
          Administrér GO-andele som en liste. Tilføj en ny periode nedenfor.
        </Typography>

        <List sx={{ mb: 2 }}>
          {entries.length === 0 && (
            <Typography variant="body2" color="text.secondary" sx={{ px: 2 }}>
              Ingen værdier endnu. Tilføj en nedenfor.
            </Typography>
          )}
          {entries.map((e, idx) => {
            const bg = (theme) => alpha(theme.palette.divider, 0.05);
            return (
              <ListItem
                sx={{ borderRadius: 2, mb: 1, bgcolor: bg }}
                key={`${e.timestamp}-${idx}`}
                secondaryAction={
                  <IconButton
                    edge="end"
                    aria-label="delete"
                    onClick={() => removeEntry(idx)}
                    sx={{ borderRadius: 2, mr: 1 }}
                  >
                    <DeleteRoundedIcon
                      sx={{ ':hover': { color: 'error.main' } }}
                    />
                  </IconButton>
                }
              >
                <ListItemText
                  primary={
                    <Stack direction="row" alignItems="center" spacing={1}>
                      <Typography variant="subtitle2">
                        {(Number(e.percentage) * 100).toFixed(1)} %
                      </Typography>
                    </Stack>
                  }
                  secondary={
                    <Typography variant="body2" color="text.secondary">
                      {DateTime.fromISO(e.timestamp).toFormat('yyyy-LL-dd')}
                    </Typography>
                  }
                />
              </ListItem>
            );
          })}
        </List>

        <Divider sx={{ my: 1 }} />

        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Tilføj ny periode
        </Typography>
        {error && (
          <Typography
            variant="caption"
            color="error"
            sx={{ mb: 1, display: 'block' }}
          >
            {error}
          </Typography>
        )}
        <Grid container spacing={2} alignItems="flex-end">
          <Grid item xs>
            <TextField
              label="GO Andel (%)"
              type="number"
              value={draft.percentage}
              onChange={(ev) =>
                setDraft((d) => ({ ...d, percentage: ev.target.value }))
              }
              variant="filled"
              fullWidth
              InputProps={{ endAdornment: <span>%</span> }}
            />
          </Grid>
          <Grid item xs>
            <TextField
              label="Gyldig fra"
              type="date"
              value={draft.date}
              onChange={(ev) =>
                setDraft((d) => ({ ...d, date: ev.target.value }))
              }
              variant="filled"
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          </Grid>
          <Grid item>
            <Tooltip title="Tilføj periode">
              <IconButton
                color="primary"
                onClick={addEntry}
                size="large"
                sx={{ mb: 0.5, borderRadius: 2 }}
              >
                <AddIcon />
              </IconButton>
            </Tooltip>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Luk</Button>
      </DialogActions>
    </Dialog>
  );
}
